import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
} from '@mui/material';
import { Add, EditOutlined, DeleteOutlined, SearchOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import boxService from '../../warehouses/services/boxService';
import { warehouseService } from '../../warehouses';
import BoxModalForm from '../../warehouses/components/BoxModalForm';
import useDebounce from '../../../hooks/useDebounce';

const CompanyBoxes = ({ companyId }) => {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const debouncedSearch = useDebounce(search, 400);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadWarehouses();
  }, [companyId]);

  useEffect(() => {
    loadBoxes();
  }, [companyId, selectedWarehouse, page, rowsPerPage, debouncedSearch]);

  const loadWarehouses = async () => {
    try {
      const response = await warehouseService.getAll({ companyId, limit: 100 });
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error al cargar bodegas:', error);
    }
  };

  const loadBoxes = async () => {
    try {
      setLoading(true);
      const response = await boxService.getAll({
        companyId,
        warehouseId: selectedWarehouse,
        limit: rowsPerPage,
        page: page + 1,
        search: debouncedSearch || undefined,
      });
      setBoxes(response.data);
      setTotal(response.pagination?.total || response.data.length);
    } catch (error) {
      showSnackbar('Error al cargar cajas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedBox(null);
    setOpenModal(true);
  };

  const handleEdit = (box) => {
    setSelectedBox(box);
    setOpenModal(true);
  };

  const handleSave = async (data) => {
    try {
      if (selectedBox) {
        await boxService.update(selectedBox.id, data);
        showSnackbar('Caja actualizada exitosamente', 'success');
      } else {
        await boxService.create({ ...data, companyId: parseInt(companyId) });
        showSnackbar('Caja creada exitosamente', 'success');
      }
      setOpenModal(false);
      loadBoxes();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar caja', 'error');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta caja?')) {
      try {
        await boxService.delete(id);
        showSnackbar('Caja eliminada exitosamente', 'success');
        loadBoxes();
      } catch (error) {
        showSnackbar('Error al eliminar caja', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Cajas de Archivo ({total})</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar por código..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchOutlined sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} /> }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtrar por Bodega</InputLabel>
            <Select
              value={selectedWarehouse}
              onChange={(e) => { setSelectedWarehouse(e.target.value); setPage(0); }}
              label="Filtrar por Bodega"
            >
              <MenuItem value="">Todas las bodegas</MenuItem>
              {warehouses.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Nueva Caja
          </Button>
        </Box>
      </Box>

      {boxes.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No hay cajas registradas</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Codigo</TableCell>
                <TableCell>Bodega</TableCell>
                <TableCell>Isla</TableCell>
                <TableCell>Estante</TableCell>
                <TableCell>Nivel</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {boxes.map((box) => (
                <TableRow
                  key={box.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/warehouses/boxes/${box.id}`)}
                >
                  <TableCell
                    sx={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 600, fontStyle: 'italic' }}
                  >
                    {box.code}
                  </TableCell>
                  <TableCell>{box.warehouse?.name || '—'}</TableCell>
                  <TableCell>{box.island || '—'}</TableCell>
                  <TableCell>{box.shelf || '—'}</TableCell>
                  <TableCell>{box.level || '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                      <IconButton sx={{ color: '#5d5d60', width: 36, height: 36 }} onClick={(e) => { e.stopPropagation(); handleEdit(box); }}>
                        <EditOutlined sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton sx={{ color: '#5d5d60', width: 36, height: 36 }} onClick={(e) => { e.stopPropagation(); handleDelete(box.id); }}>
                        <DeleteOutlined sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      )}

      <BoxModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        box={selectedBox}
        warehouses={warehouses}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyBoxes;
